/**
 * Transform commands (W2-11 batch-tools; DESIGN.md §5.4; WORKPACKAGES Appendix B `transform.*`):
 *  - `transform.open`: the numeric Transform dialog over the selected notes and keys (pivot: the playhead);
 *  - `transform.reverseTime`: notes and segments mirrored in time within the selection's span (easings reversed
 *    exactly, `Core.transform.reverseTime`);
 *  - `transform.stretch` / `transform.squeeze`: double or halve the selection's length around its start.
 */

import { DIALOG } from '../../app/dialogs/types';
import type { CommandContext, CommandDef } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import { m } from './batch.messages';
import { playheadBeat, ref, selectionIds, transactWithToasts, type Enabled } from './batchContext';
import { selectedSegments } from './segmentOps';

function needItems(ctx: CommandContext): Enabled {
  if (ctx.store === null) return ref(m.reasonNoProject);
  const ids = selectionIds(ctx);
  return ids.notes.length + ids.keyish.length > 0 ? true : ref(m.reasonNothingSelected);
}

function scaleSelection(ctx: CommandContext, p: number, q: number, command: string): void {
  const ids = selectionIds(ctx);
  transactWithToasts(
    ctx,
    { label: ref(p > q ? m.txnStretch : m.txnSqueeze), command },
    (tx) => {
      Core.transform.transform(tx, { notes: ids.notes, keys: ids.keyish, spec: { timeScale: [p, q] } });
    },
    () => ref(m.transformDone, { count: ids.notes.length + ids.keyish.length }),
  );
}

export const TRANSFORM_COMMANDS: readonly CommandDef[] = [
  {
    id: 'transform.open',
    title: m.cmdTransform,
    category: 'Edit',
    icon: 'Move',
    aliases: ['transform', 'scale time', 'offset', 'stretch'],
    level: 'guided',
    enabled: needItems,
    async run(ctx) {
      const ids = selectionIds(ctx);
      await ctx.ui.openDialog(DIALOG.transform, { notes: ids.notes, keys: ids.keyish, pivotBeat: playheadBeat(ctx) });
    },
    menus: [
      { menu: 'lane.context', group: 'transform', order: 10 },
      { menu: 'motion.context', group: 'transform', order: 10 },
      { menu: 'tools.menu', group: 'transform', order: 10 },
      { menu: 'app.edit', group: 'transform', order: 10 },
    ],
  },
  {
    id: 'transform.reverseTime',
    title: m.cmdReverseTime,
    category: 'Edit',
    icon: 'ArrowLeftRight',
    aliases: ['reverse', 'retrograde'],
    level: 'guided',
    enabled: needItems,
    run(ctx) {
      const ids = selectionIds(ctx);
      const segments = selectedSegments(ctx);
      transactWithToasts(
        ctx,
        { label: ref(m.txnReverseTime), command: 'transform.reverseTime' },
        (tx) => {
          Core.transform.reverseTime(tx, { notes: ids.notes, segments });
        },
        () => ref(m.reverseTimeDone, { count: ids.notes.length + segments.length }),
      );
    },
    menus: [
      { menu: 'lane.context', group: 'transform', order: 20 },
      { menu: 'app.edit', group: 'transform', order: 20 },
    ],
  },
  {
    id: 'transform.stretch',
    title: m.cmdStretch,
    category: 'Edit',
    icon: 'Maximize2',
    aliases: ['half speed', 'double length', '2x'],
    level: 'full',
    enabled: needItems,
    run(ctx) {
      scaleSelection(ctx, 2, 1, 'transform.stretch');
    },
    menus: [{ menu: 'app.edit', group: 'transform', order: 30 }],
  },
  {
    id: 'transform.squeeze',
    title: m.cmdSqueeze,
    category: 'Edit',
    icon: 'Minimize2',
    aliases: ['double speed', 'half length', '0.5x'],
    level: 'full',
    enabled: needItems,
    run(ctx) {
      scaleSelection(ctx, 1, 2, 'transform.squeeze');
    },
    menus: [{ menu: 'app.edit', group: 'transform', order: 31 }],
  },
];
